import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ExportDeclarationIncomeForm from './ExportDeclarationIncomeForm';
import api from '../api/api';
// eslint-disable-next-line react/prop-types
const DeclarationIncomeForm = ({ idBiodata, booking_id }) => {
  const [formData, setFormData] = useState({
    name: '',
    nric: '',
    residence: '',
    zip: '',
    mobile: '',
    email: '',
    nationality: '',
    occupation: '',
    company: '',
    companyAddress: '',
    contact: '',
    income: '',
    ageGroup1: '',
    ageGroup2: '',
    ageGroup3: '',
    staff: '',
    signature: '',
    date: '',
    consentName: '',
    consentDate: ''
  });

  useEffect(() => {
    // Fetch data for the booking_id
    fetchDeclarationIncomeData();
  }, [booking_id]);

  const fetchDeclarationIncomeData = async () => {
    if (!booking_id) {
      console.log('No booking_id provided, keeping form initialized');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching Declaration Income Form data for booking_id:', booking_id);
      const response = await api.get(`external-forms/declaration-income-forms/${booking_id}/`);
      
      if (response.data) {
        console.log('Declaration Income Form data retrieved:', response.data);
        
        // Map backend field names to frontend form fields
        const backendData = response.data;
        setFormData({
          name: backendData.name || '',
          nric: backendData.nric || '',
          residence: backendData.residence || '',
          zip: backendData.zip || '',
          mobile: backendData.mobile || '',
          email: backendData.email || '',
          nationality: backendData.nationality || '',
          occupation: backendData.occupation || '',
          company: backendData.company || '',
          companyAddress: backendData.company_address || backendData.companyAddress || '',
          contact: backendData.contact || '',
          income: backendData.income || '',
          ageGroup1: backendData.age_group_1?.toString() || backendData.ageGroup1?.toString() || '',
          ageGroup2: backendData.age_group_2?.toString() || backendData.ageGroup2?.toString() || '',
          ageGroup3: backendData.age_group_3?.toString() || backendData.ageGroup3?.toString() || '',
          staff: backendData.staff?.toString() || '',
          signature: backendData.signature || '',
          date: backendData.date || '',
          consentName: backendData.consent_name || backendData.consentName || '',
          consentDate: backendData.consent_date || backendData.consentDate || ''
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('No existing Declaration Income Form found for booking_id:', booking_id, '- keeping form initialized');
        // Keep the form in its initial state - no need to do anything
      } else {
        console.error('Error fetching Declaration Income Form data:', error);
        // Keep the form in its initial state even if there's an error
      }
    } finally {
      setIsLoading(false);
    }
  };

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // All fields are now optional
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for API submission, mapping frontend fields to backend fields
      const apiData = {
        booking: booking_id,
        maid: idBiodata,
        name: formData.name,
        nric: formData.nric,
        residence: formData.residence,
        zip: formData.zip,
        mobile: formData.mobile,
        email: formData.email,
        nationality: formData.nationality,
        occupation: formData.occupation,
        company: formData.company,
        company_address: formData.companyAddress,
        contact: formData.contact,
        income: formData.income,
        age_group_1: parseInt(formData.ageGroup1) || 0,
        age_group_2: parseInt(formData.ageGroup2) || 0,
        age_group_3: parseInt(formData.ageGroup3) || 0,
        staff: parseInt(formData.staff) || 0,
        signature: formData.signature,
        date: formData.date || null,
        consent_name: formData.consentName,
        consent_date: formData.consentDate || null
      };

      console.log('Submitting Declaration Income Form Data:', apiData);
      
      const response = await api.post('external-forms/declaration-income-forms/', apiData);
      console.log('Declaration Income Form submitted successfully:', response.data);
      
      // Show success message
      alert('Declaration Income Form submitted successfully!');
      
      // Clear any errors
      setErrors({});
      
      // Optionally reset form after successful submission
      // handleReset();
      
    } catch (error) {
      console.error('Error submitting Declaration Income Form:', error);
      
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data);
        
        // Handle field-specific errors from backend
        if (typeof error.response.data === 'object') {
          setErrors(error.response.data);
        } else {
          alert(`Error submitting form: ${JSON.stringify(error.response.data)}`);
        }
      } else {
        alert('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setErrors({}); // Clear errors
    setFormData({
      name: '',
      nric: '',
      residence: '',
      zip: '',
      mobile: '',
      email: '',
      nationality: '',
      occupation: '',
      company: '',
      companyAddress: '',
      contact: '',
      income: '',
      ageGroup1: '',
      ageGroup2: '',
      ageGroup3: '',
      staff: '',
      signature: '',
      date: '',
      consentName: '',
      consentDate: ''
    });
  };

  const handleDelete = async () => {
    if (!booking_id) {
      alert('No booking ID provided for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this Declaration Income Form record? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Deleting Declaration Income Form for booking_id:', booking_id);
      const response = await api.delete(`external-forms/declaration-income-forms/${booking_id}/`);
      console.log('Declaration Income Form deleted successfully:', response.data);
      
      // Show success message
      alert('Declaration Income Form record deleted successfully!');
      
      // Reset the form after deletion
      handleReset();
      
      // Clear any errors
      setErrors({});
      
    } catch (error) {
      console.error('Error deleting Declaration Income Form:', error);
      
      if (error.response && error.response.status === 404) {
        alert('Declaration Income Form record deleted successfully!');
      } else if (error.response && error.response.data) {
        console.error('Error details:', error.response.data);
        alert('Error deleting record: ' + (error.response.data.error || 'Unknown error'));
      } else {
        alert('Error deleting record. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { user } = useAuth();
  const [agencyInfo, setAgencyInfo] = useState({});
  const handleDownloadPDF = async () => {
    setIsSubmitting(true);

    try {
      console.log('Generating Declaration Income Form PDF for booking_id:', booking_id);
      
      // Get form details if needed
      let responseDetails = null;
      if (booking_id) {
        try {
          // Try to get additional details from the backend if needed
          responseDetails = await api.get(`external-forms/declaration-income-forms/${booking_id}/`);
            setAgencyInfo({
          agencyAddress: responseDetails.data.agency_address || '',
          agencyPhone: responseDetails.data.agency_phone || ''
        });
       } catch (error) {
          console.log('Could not fetch additional details, using form data only');
        }
      }
      
      // Prepare the data for PDF generation
      const pdfData = {
        ...formData,
        booking_id: booking_id
      };

      // Generate PDF using React PDF
      const blob = await pdf(
        <ExportDeclarationIncomeForm
          user={user}
          data={pdfData}
          agencyInfo={agencyInfo}
        />
      ).toBlob();
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = booking_id 
        ? `Declaration_Income_Form_${booking_id}_${today}.pdf`
        : `Declaration_Income_Form_${today}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Declaration Income Form PDF generated and downloaded successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary me-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="text-muted">Loading existing declaration data...</span>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper" style={{ marginLeft: '0' }}>
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="content-page-header">
                    <h5>MWOSG Form 01-D - Information Sheet of Employer</h5>
                  </div>
                  <p className="text-muted small mb-3">
                    <em>Instructions: This form shall be completed by the DIRECT Employer of the OFW. Please fill out each field when applicable, otherwise write NA.</em>
                  </p>

                  {/* Error Display */}
                  {Object.keys(errors).length > 0 && (
                    <div className="alert alert-danger">
                      <h6 className="mb-2">Please fix the following errors:</h6>
                      <ul className="mb-0">
                        {Object.entries(errors).map(([field, error]) => (
                          <li key={field}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    {/* Personal Information Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Personal Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Name</label>
                              <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                              />
                              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>NRIC</label>
                              <input
                                type="text"
                                name="nric"
                                value={formData.nric}
                                onChange={handleInputChange}
                                className={`form-control ${errors.nric ? 'is-invalid' : ''}`}
                              />
                              {errors.nric && <div className="invalid-feedback">{errors.nric}</div>}
                            </div>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Residence Address</label>
                              <input
                                type="text"
                                name="residence"
                                value={formData.residence}
                                onChange={handleInputChange}
                                className={`form-control ${errors.residence ? 'is-invalid' : ''}`}
                              />
                              {errors.residence && <div className="invalid-feedback">{errors.residence}</div>}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Zip Code</label>
                              <input
                                type="text"
                                name="zip"
                                value={formData.zip}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Mobile</label>
                              <input
                                type="text"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleInputChange}
                                className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                              />
                              {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Email</label>
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                              />
                              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                            </div>
                          </div>
                        </div>
                        
                        <div className="input-block mb-3">
                          <label>Nationality</label>
                          <input
                            type="text"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleInputChange}
                            className={`form-control ${errors.nationality ? 'is-invalid' : ''}`}
                          />
                          {errors.nationality && <div className="invalid-feedback">{errors.nationality}</div>}
                        </div>
                      </div>
                    </div>

                    {/* Employment Information Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employment Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Occupation</label>
                              <input
                                type="text"
                                name="occupation"
                                value={formData.occupation}
                                onChange={handleInputChange}
                                className={`form-control ${errors.occupation ? 'is-invalid' : ''}`}
                              />
                              {errors.occupation && <div className="invalid-feedback">{errors.occupation}</div>}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Company</label>
                              <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Company Address</label>
                              <input
                                type="text"
                                name="companyAddress"
                                value={formData.companyAddress}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Contact</label>
                              <input
                                type="text"
                                name="contact"
                                value={formData.contact}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Family Information Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Family Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="input-block mb-3">
                          <label>Monthly Family Income</label>
                          <input
                            type="number"
                            name="income"
                            value={formData.income}
                            onChange={handleInputChange}
                            className={`form-control ${errors.income ? 'is-invalid' : ''}`}
                            min="0"
                            step="0.01"
                          />
                          {errors.income && <div className="invalid-feedback">{errors.income}</div>}
                        </div>
                        
                        <label className="mb-2">Number of Members in the Household by Age Group</label>
                        <div className="row">
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>0-20 years old</label>
                              <input
                                type="number"
                                name="ageGroup1"
                                value={formData.ageGroup1}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>21-65 years old</label>
                              <input
                                type="number"
                                name="ageGroup2"
                                value={formData.ageGroup2}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>65 years old and above</label>
                              <input
                                type="number"
                                name="ageGroup3"
                                value={formData.ageGroup3}
                                onChange={handleInputChange}
                                className="form-control"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="input-block mb-3">
                          <label>Total Number of Staff in the Company</label>
                          <input
                            type="number"
                            name="staff"
                            value={formData.staff}
                            onChange={handleInputChange}
                            className="form-control"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Signature Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employer Signature</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Name and Signature</label>
                              <input
                                type="text"
                                name="signature"
                                value={formData.signature}
                                onChange={handleInputChange}
                                className={`form-control ${errors.signature ? 'is-invalid' : ''}`}
                              />
                              {errors.signature && <div className="invalid-feedback">{errors.signature}</div>}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                              />
                              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Consent Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Consent for Data Processing</h6>
                      </div>
                      <div className="card-body">
                        <div className="alert alert-info">
                          <p className="mb-2">
                            I hereby consent to the collection and processing of my personal data by the Department of Migrant Workers for the purpose of evaluating my application and other related services.
                          </p>
                          <p className="mb-0">
                            I understand that my personal data will be handled in accordance with the Data Privacy Act of 2012 and other applicable laws.
                          </p>
                        </div>
                        
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Name and Signature</label>
                              <input
                                type="text"
                                name="consentName"
                                value={formData.consentName}
                                onChange={handleInputChange}
                                className={`form-control ${errors.consentName ? 'is-invalid' : ''}`}
                              />
                              {errors.consentName && <div className="invalid-feedback">{errors.consentName}</div>}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input
                                type="date"
                                name="consentDate"
                                value={formData.consentDate}
                                onChange={handleInputChange}
                                className={`form-control ${errors.consentDate ? 'is-invalid' : ''}`}
                              />
                              {errors.consentDate && <div className="invalid-feedback">{errors.consentDate}</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Buttons */}
                    <div className="row">
                      <div className="col-12">
                        <div className="btn-path text-end">
                          <button
                            type="button"
                            onClick={handleReset}
                            className="btn btn-cancel bg-primary-light me-3"
                            disabled={isSubmitting}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger me-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Deleting...' : 'Clean'}
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadPDF}
                            className="btn btn-success me-3"
                            disabled={isSubmitting}
                            title="Download PDF of current form data"
                          >
                            {isSubmitting ? 'Generating...' : (
                              <>
                                <i className="fas fa-download me-2"></i>
                                Download PDF
                              </>
                            )}
                          </button>
                          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeclarationIncomeForm;